// react-table is extending from object, so we have to do that as well
/* eslint-disable @typescript-eslint/ban-types */
import React, { useMemo } from 'react';
import { Row } from 'react-table';

import { GroupedRowWithSubHeader } from './components';

interface GroupedRowsWithSubHeadersProps<D extends object> {
    /**
     * Width for the caret icon
     * @defaultValue 10
     */
    caretIconWidth?: number;
    /**
     * If a row is grouped by this column ID, it will be treated as an expanding row and rendered
     * as a top-level row.
     */
    expandingGroupById: string;
    /**
     * Tailwind size to apply to text for grouped rows
     * @defaultValue 'sm'
     */
    groupTextSize?: 'xs' | 'sm' | 'base' | 'lg';
    /**
     * The `flatRows` array returned by the react-table `useTable()` effect
     */
    flatRows: Row<D>[];
    /**
     * Outer row height
     */
    rowHeight: number;
    /**
     * Sub-header height
     */
    subHeaderHeight: number;
    /**
     * Sub-row height
     */
    subRowHeight: number;
    /**
     * The `prepareRow()` function returned by the react-table `useTable()` effect
     */
    prepareRow(row: Row<D>): void;
    /**
     * Tailwind size to apply to text
     * @defaultValue 'xs'
     */
    textSize?: 'xs' | 'sm' | 'base' | 'lg';
}

/**
 * Renders table rows as grouped rows that are expandable. Each grouped row has a caret indicating
 * whether it is expanded or not.
 *
 * @remarks
 * To use this component, the react-table `useTable` hook must be passed the `useExpanded` and
 * `useGroupBy` hooks. Not intended for use within a render function for <FixedSizeList> from
 * react-window.
 * @param props - {@link GroupedRowsWithSubHeadersProps}
 */
export function GroupedRowsWithSubHeaders<D extends object>({
    caretIconWidth = 10,
    expandingGroupById,
    subHeaderHeight,
    subRowHeight,
    groupTextSize = 'sm',
    flatRows,
    rowHeight,
    prepareRow,
    textSize = 'xs',
}: GroupedRowsWithSubHeadersProps<D>): JSX.Element {
    const expandingRows = useMemo(() => {
        let offset = 0;

        return flatRows.reduce(
            (acc: ExpandingRow<D>[], row, index): ExpandingRow<D>[] => {
                if ((row?.groupByID ?? '') === expandingGroupById) {
                    // row.groupByID is only defined for expanding rows. We need to keep track of the
                    // original row index here so we can correctly specify aria-rowindex within
                    // sub-rows.
                    acc.push({
                        row,
                        index: index + offset,
                    });

                    offset += 1;
                }

                return acc;
            },
            [],
        );
    }, [flatRows]);

    return (
        <>
            {expandingRows.map(
                ({ row, index }: ExpandingRow<D>): JSX.Element => {
                    prepareRow(row);
                    const { key: rowKey } = row.getRowProps();

                    return (
                        <GroupedRowWithSubHeader<D>
                            key={rowKey}
                            caretIconWidth={caretIconWidth}
                            groupTextSize={groupTextSize}
                            ariaRowIndex={index + 1}
                            subHeaderHeight={subHeaderHeight}
                            subRowHeight={subRowHeight}
                            rowHeight={rowHeight}
                            prepareRow={prepareRow}
                            row={row}
                            textSize={textSize}
                        />
                    );
                },
            )}
        </>
    );
}
